import { useMemo } from "react";
import generateId from "logic/generateId";
import { createElasticsearchQueryString } from "views/logic/queries/Query";
import AggregationWidget from "views/logic/aggregationbuilder/AggregationWidget";
import AggregationWidgetConfig from "views/logic/aggregationbuilder/AggregationWidgetConfig";
import Series from "views/logic/aggregationbuilder/Series";
import { useTranslation } from "react-i18next";
import Pivot from "views/logic/aggregationbuilder/Pivot";
import pivotForField from "views/logic/searchtypes/aggregation/PivotGenerator";
import FieldType from "views/logic/fieldtypes/FieldType";
import AreaVisualizationConfig from "views/logic/aggregationbuilder/visualizations/AreaVisualizationConfig";
import WidgetPosition from "views/logic/widgets/WidgetPosition";
import { TIMESTAMP_FIELD } from "views/Constants";
import useWelcomeSearch, {
  numberWidget,
  messagesTodayLink,
  alertsOrEventsLink
} from "./useWelcomeSearch";
import useMetricsTimeRange from "./useMetricsTimeRange";
import usePermittedAlertsEventsStreams from "./usePermittedAlertsEventsStreams";
const topSourcesWidget = (timeRange, t) => ({
  title: t("metrics.top_sources"),
  widget: AggregationWidget.builder().id(generateId()).timerange(timeRange).query(createElasticsearchQueryString("")).config(
    AggregationWidgetConfig.builder().rowPivots([pivotForField(TIMESTAMP_FIELD, new FieldType("date", [], []))]).columnPivots([Pivot.createValues(["source"], { limit: 5, skip_empty_values: false })]).series([Series.forFunction("count()")]).visualization("area").visualizationConfig(AreaVisualizationConfig.create("spline")).rollup(false).build()
  ).build()
});
const buildEntries = (permittedAlertsEventsStreams, timeRange, topSourcesOnly, t) => {
  const sources = topSourcesWidget(timeRange, t);
  if (topSourcesOnly) {
    return [{ ...sources, position: new WidgetPosition(1, 1, 3, 12) }];
  }
  const messages = numberWidget({ title: t("metrics.messages_today"), timeRange, link: messagesTodayLink(timeRange) });
  if (permittedAlertsEventsStreams.length === 0) {
    return [
      { ...messages, position: new WidgetPosition(1, 1, 3, 4) },
      { ...sources, position: new WidgetPosition(5, 1, 3, 8) }
    ];
  }
  const numberWidgets = [
    messages,
    numberWidget({
      title: t("metrics.alerts_today"),
      timeRange,
      link: alertsOrEventsLink(timeRange, true),
      queryString: "alert:true",
      streams: permittedAlertsEventsStreams
    }),
    numberWidget({
      title: t("metrics.events_today"),
      timeRange,
      link: alertsOrEventsLink(timeRange, false),
      queryString: "alert:false",
      streams: permittedAlertsEventsStreams
    })
  ];
  return [
    ...numberWidgets.map(({ title, widget }, index) => ({
      title,
      widget,
      position: new WidgetPosition(1 + index * 4, 1, 1.6, 4)
    })),
    { ...sources, position: new WidgetPosition(1, 2.75, 3, 12) }
  ];
};
const useGeneralMetricsSearch = (topSourcesOnly = false) => {
  const { t } = useTranslation("welcome");
  const rangeSeconds = useMetricsTimeRange();
  const permittedAlertsEventsStreams = usePermittedAlertsEventsStreams();
  const timeRange = useMemo(() => ({ type: "relative", from: rangeSeconds }), [rangeSeconds]);
  const entries = useMemo(
    () => buildEntries(permittedAlertsEventsStreams, timeRange, topSourcesOnly, t),
    [permittedAlertsEventsStreams, timeRange, topSourcesOnly, t]
  );
  return useWelcomeSearch(entries, timeRange);
};
export default useGeneralMetricsSearch;
